// US-AUTH-01: account selection screen -- the picker.
//
// Two-pane layout per docs/design/SPEC.md S.1 (screens/01-picker.png):
//   * Left  -- warm brand pane: wordmark, time-aware greeting, tagline, offline footer.
//   * Right -- "Pick your profile", grouped Guardians + Kids, footer actions.
//
// Copy comes from `enUS.picker` and `enUS.brand` in @chorepop/core/i18n.
// Each account tile keeps its display name in the accessible name so
// existing Playwright selectors like `getByRole('button', { name: /sarah/i })`
// continue to resolve.

import { enUS, greetingFor } from '@chorepop/core';
import { type AccountListEntry, accountsList, deviceRegister } from '@chorepop/core/client';
import {
  type Component,
  For,
  Match,
  Show,
  Switch,
  createResource,
  onCleanup,
  onMount,
} from 'solid-js';
import { AppIcon } from '../components/AppIcon';
import { AppVersionFooter } from '../components/AppVersionFooter';
import { ChorePopMark } from '../components/ChorePopMark';

type Props = {
  onAccountPicked: (account: AccountListEntry) => void;
  /** "+ Add a child" footer link. If omitted, the button is hidden. */
  onAddChild?: (() => void) | undefined;
  /** "Settings" footer link. If omitted, the button is hidden. */
  onOpenSettings?: (() => void) | undefined;
  /** Optional ribbon shown after a fresh setup or a successful logout. */
  welcomeMessage?: string | undefined;
  /**
   * US-AUTH-09 -- overlay modal for picking which guardian to sign in
   * as for a picker-footer action ("+ Add a child" / Settings) when more
   * than one guardian exists. Set by
   * App.tsx's `guardianPickerIntent` signal -- the picker stays
   * visible underneath, dimmed by the overlay, so the user has
   * spatial context.
   *
   * Single-guardian families never receive this prop -- the footer
   * button routes them straight to login. Zero-guardian families
   * see the existing `welcomeMessage` "no guardian found" banner
   * instead. The modal is therefore strictly additive: passing
   * `undefined` leaves the screen behaving exactly as before.
   */
  guardianPicker?:
    | {
        intent: 'addChild' | 'settings';
        guardians: AccountListEntry[];
        onPick: (guardian: AccountListEntry) => void;
        onCancel: () => void;
      }
    | undefined;
  /** Shown on child-assigned devices: opens the guardian credential
   *  challenge to access device settings. */
  onParentSettings?: (() => void) | undefined;
};

export const AccountSelectionScreen: Component<Props> = (props) => {
  const [accountsResource] = createResource(async () => {
    const result = await accountsList();
    if (!result.ok) {
      throw new Error(result.error.message);
    }
    return result.value;
  });

  // Check device assignment on startup -- filters the account list to a
  // single child if this device has been assigned.
  const [deviceAssignment] = createResource(async () => {
    const r = await deviceRegister({
      deviceName: navigator.userAgent.includes('Android')
        ? 'Android device'
        : navigator.userAgent.includes('Win')
          ? 'Windows device'
          : 'Device',
    });
    return r.ok ? r.value.assignment : null;
  });

  // If this device is assigned to a specific child, only show that child.
  // All accounts are still in the local DB for guardian credential validation.
  const visibleAccounts = () => {
    const all = accountsResource() ?? [];
    const assignment = deviceAssignment();
    if (assignment?.assignedAccountId && !assignment.isRevoked) {
      return all.filter((a) => a.accountId === assignment.assignedAccountId);
    }
    return all;
  };

  const isChildDevice = () => {
    const assignment = deviceAssignment();
    return !!(assignment?.assignedAccountId && !assignment.isRevoked);
  };

  const guardians = () => visibleAccounts().filter((a) => a.roleCode === 'guardian');
  const kids = () => visibleAccounts().filter((a) => a.roleCode === 'child');

  return (
    <>
      <main class="picker-screen">
        {/* Left brand pane */}
        <aside class="picker-brand">
          <div class="picker-brand-mark">
            <ChorePopMark class="picker-brand-glyph" />
            <span class="picker-brand-name" aria-label={enUS.brand.name}>
              <span>Chore</span>
              <span class="picker-brand-name-pop">Pop</span>
            </span>
          </div>

          <div class="picker-brand-greeting">
            <h2 class="picker-brand-greeting-text h-display">{greetingFor()}.</h2>
            <p class="picker-brand-tagline">{enUS.brand.tagline}</p>
          </div>

          <div class="picker-brand-footer">
            <AppIcon name="lock" />
            <span>{enUS.picker.sync}</span>
          </div>
        </aside>

        {/* Right account pane */}
        <section class="picker-accounts">
          <header class="picker-accounts-header">
            <p class="picker-eyebrow">Who's here?</p>
            <h1 class="picker-title h-display">{enUS.picker.title}</h1>
          </header>

          <Show when={props.welcomeMessage}>
            {(msg) => (
              <p class="picker-welcome" role="status">
                {msg()}
              </p>
            )}
          </Show>

          <Switch>
            <Match when={accountsResource.loading}>
              <p class="picker-placeholder">Loading accounts…</p>
            </Match>

            <Match when={accountsResource.error}>
              <p class="picker-placeholder picker-error" role="alert">
                Could not load accounts: {(accountsResource.error as Error).message}
              </p>
            </Match>

            <Match when={accountsResource()?.length === 0}>
              <p class="picker-placeholder">
                No accounts on this device yet. (This shouldn't happen -- please close the app and
                reopen.)
              </p>
            </Match>

            <Match when={accountsResource()}>
              {/* BUG-017: tile groups scroll independently so only this section
                grows tall when there are many accounts; chrome (heading,
                welcome banner, footer actions) stays put. */}
              <div class="picker-tiles-scroll">
                <Show when={guardians().length > 0}>
                  <div class="picker-group picker-group--guardian">
                    <p class="picker-group-label">▲ {enUS.picker.parents}</p>
                    <ul class="picker-tiles picker-tiles--guardians">
                      <For each={guardians()}>
                        {(account) => (
                          <li>
                            <ProfileTile account={account} onPick={props.onAccountPicked} />
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                </Show>

                <Show when={kids().length > 0}>
                  <div class="picker-group picker-group--child">
                    <p class="picker-group-label">● {enUS.picker.kids}</p>
                    <ul class="picker-tiles picker-tiles--kids">
                      <For each={kids()}>
                        {(account) => (
                          <li>
                            <ProfileTile account={account} onPick={props.onAccountPicked} />
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                </Show>
              </div>

              <nav class="picker-footer-actions">
                <Show when={props.onAddChild}>
                  <button
                    type="button"
                    class="picker-link picker-link--accent"
                    onClick={() => props.onAddChild?.()}
                    data-testid="picker-add-child"
                  >
                    {enUS.picker.addChild}
                  </button>
                  <Show when={props.onOpenSettings}>
                    <span aria-hidden="true">·</span>
                  </Show>
                </Show>
                <Show when={props.onOpenSettings}>
                  <button
                    type="button"
                    class="picker-link"
                    onClick={() => props.onOpenSettings?.()}
                    data-testid="picker-settings"
                  >
                    {enUS.picker.settings}
                  </button>
                </Show>
              </nav>
            </Match>
          </Switch>

          {/* Parent settings link -- only shown on child-assigned devices */}
          <Show when={isChildDevice() && props.onParentSettings}>
            <div class="picker-parent-settings">
              <button
                type="button"
                class="picker-link"
                onClick={() => props.onParentSettings?.()}
                data-testid="picker-parent-settings"
              >
                Parent settings
              </button>
            </div>
          </Show>

          <AppVersionFooter />
        </section>
      </main>
      <Show when={props.guardianPicker} keyed>
        {(picker) => <GuardianPickerModal config={picker} />}
      </Show>
    </>
  );
};

const ProfileTile: Component<{
  account: AccountListEntry;
  onPick: (a: AccountListEntry) => void;
}> = (props) => {
  const initials = () =>
    props.account.displayName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.slice(0, 1).toUpperCase())
      .join('');

  // BUG-017: removed the .picker-tile-subtitle that duplicated the role.
  // The role badge below the name is the only role indicator now -- it
  // lives inside .picker-tile-body in normal flow, not absolutely
  // positioned in the corner. Same chip style as the old corner badge.
  //
  // Name parts (BUG-017 follow-up -- uniform tile heights): each
  // whitespace-separated word in the display name renders on its own
  // line. This makes wrapping deterministic at space boundaries (no
  // mid-word breaks), and combined with a `min-height` on `.picker-tile`
  // sized for two name lines + chip, every tile in a row is the same
  // height regardless of whether the name has one or two words.
  const nameParts = () => props.account.displayName.trim().split(/\s+/).filter(Boolean);

  return (
    <button
      type="button"
      class={`picker-tile picker-tile--${props.account.roleCode}`}
      onClick={() => props.onPick(props.account)}
    >
      <span class="picker-tile-avatar" aria-hidden="true">
        {initials()}
      </span>
      <span class="picker-tile-body">
        <span class="picker-tile-name h-display">
          <For each={nameParts()}>
            {(part) => <span class="picker-tile-name-word">{part}</span>}
          </For>
        </span>
        <span class="picker-tile-role">
          {props.account.roleCode === 'guardian' ? 'GUARDIAN' : 'CHILD'}
        </span>
      </span>
    </button>
  );
};

/**
 * US-AUTH-09 -- overlay modal that lets a user choose which guardian to
 * sign in as for a picker-footer action ("+ Add a child" / Settings) when
 * more than one guardian exists in the family.
 *
 * Lives inside this file because its only consumer is the picker; the
 * guardian list, role chip styling, and overall design language are
 * deliberately copied from the picker tiles to keep visual continuity.
 *
 * Keyboard: Escape cancels. The first guardian button receives focus on
 * mount so Tab cycles through the list and Enter activates the
 * focused button immediately. Backdrop click also cancels.
 */
const GuardianPickerModal: Component<{
  config: NonNullable<Props['guardianPicker']>;
}> = (props) => {
  const action = () => (props.config.intent === 'addChild' ? 'add a child' : 'open Settings');

  // Esc to cancel -- registered on mount, removed on unmount so leaving
  // the picker (e.g. via guardian-pick -> login route) doesn't leave a
  // dangling listener that captures Escape on later screens.
  const handleKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      props.config.onCancel();
    }
  };
  onMount(() => window.addEventListener('keydown', handleKey));
  onCleanup(() => window.removeEventListener('keydown', handleKey));

  let firstButton: HTMLButtonElement | undefined;
  onMount(() => firstButton?.focus());

  return (
    <div
      class="guardian-picker-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guardian-picker-title"
      onClick={(e) => {
        // Backdrop clicks (target === current) cancel; clicks inside
        // the dialog body do not bubble up to here because of the inner
        // stopPropagation on the .guardian-picker-modal div.
        if (e.target === e.currentTarget) {
          props.config.onCancel();
        }
      }}
    >
      <div class="guardian-picker-modal" onClick={(e) => e.stopPropagation()}>
        <h2 id="guardian-picker-title" class="guardian-picker-title h-display">
          Sign in as a guardian to {action()}
        </h2>
        <p class="guardian-picker-sub">Choose your account to continue.</p>

        <ul class="guardian-picker-list">
          <For each={props.config.guardians}>
            {(g, i) => {
              const nameParts = g.displayName.trim().split(/\s+/).filter(Boolean);
              return (
                <li>
                  <button
                    type="button"
                    class="picker-tile picker-tile--guardian"
                    onClick={() => props.config.onPick(g)}
                    data-testid={`guardian-picker-${g.accountId}`}
                    ref={(el) => {
                      if (i() === 0) firstButton = el;
                    }}
                  >
                    <span class="picker-tile-avatar" aria-hidden="true">
                      {nameParts
                        .slice(0, 2)
                        .map((p) => p.slice(0, 1).toUpperCase())
                        .join('')}
                    </span>
                    <span class="picker-tile-body">
                      <span class="picker-tile-name h-display">
                        <For each={nameParts}>
                          {(part) => <span class="picker-tile-name-word">{part}</span>}
                        </For>
                      </span>
                      <span class="picker-tile-role">GUARDIAN</span>
                    </span>
                  </button>
                </li>
              );
            }}
          </For>
        </ul>

        <div class="guardian-picker-actions">
          <button
            type="button"
            class="ghost"
            onClick={() => props.config.onCancel()}
            data-testid="guardian-picker-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
